import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import AuthApi from '../../service/Api/authApi';

/**
 * Saved tarot readings for the History → Love Tarot Readings tab
 * (HORO-10647). Kept out of chatsSlice so the chat list isn't affected.
 */
const initialState = {
    list: [],
    page: 1,
    totalPages: 0,
    loading: false,
    // True once the first page has resolved, so the tab can tell "loading" from "empty".
    loaded: false,
    error: null
};

export const fetchTarotReadings = createAsyncThunk('tarotReadings/fetch', async ({ variant = 'love', page = 1 }, { rejectWithValue }) => {
    try {
        const response = await AuthApi.getTarotReadings({ variant, page });
        return response.data;
    } catch (error) {
        return rejectWithValue(error?.response?.status || true);
    }
});

// Optimistic: the vote shows immediately and is rolled back if the save fails.
export const setTarotReadingFeedback = createAsyncThunk('tarotReadings/setFeedback', async ({ id, feedback }, { rejectWithValue }) => {
    try {
        await AuthApi.setTarotReadingFeedback(id, feedback);
        return { id, feedback };
    } catch (error) {
        return rejectWithValue(error?.response?.status || true);
    }
});

const tarotReadingsSlice = createSlice({
    name: 'tarotReadings',
    initialState,
    reducers: {
        resetTarotReadings: () => initialState
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTarotReadings.pending, (state, action) => {
                state.loading = true;
                state.error = null;
                if ((action.meta.arg.page || 1) === 1) {
                    state.list = [];
                    state.loaded = false;
                }
            })
            .addCase(fetchTarotReadings.fulfilled, (state, action) => {
                const { data = [], pagination = {} } = action.payload || {};
                state.loading = false;
                state.loaded = true;
                state.page = pagination.page || action.meta.arg.page || 1;
                state.totalPages = pagination.total_pages || 0;
                state.list = state.page === 1 ? data : [...state.list, ...data];
            })
            .addCase(setTarotReadingFeedback.pending, (state, action) => {
                const reading = state.list.find((r) => r.id === action.meta.arg.id);
                if (reading) {
                    // Remember the vote to restore if the save fails.
                    reading.previousFeedback = reading.feedback ?? null;
                    reading.feedback = action.meta.arg.feedback;
                }
            })
            .addCase(setTarotReadingFeedback.fulfilled, (state, action) => {
                const reading = state.list.find((r) => r.id === action.payload.id);
                if (reading) delete reading.previousFeedback;
            })
            .addCase(setTarotReadingFeedback.rejected, (state, action) => {
                const reading = state.list.find((r) => r.id === action.meta.arg.id);
                if (reading) {
                    reading.feedback = reading.previousFeedback ?? null;
                    delete reading.previousFeedback;
                    // 404: the reading no longer exists, so there is nothing to vote on.
                    if (action.payload === 404) reading.feedbackDisabled = true;
                }
            })
            .addCase(fetchTarotReadings.rejected, (state, action) => {
                state.loading = false;
                state.loaded = true;
                state.error = action.payload || true;
            });
    }
});

export const { resetTarotReadings } = tarotReadingsSlice.actions;
export const getTarotReadingsState = (state) => state.tarotReadings;
export const tarotReadingsReducer = tarotReadingsSlice.reducer;
